import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import Seo from './Seo';

const services = [
  {
    id: 'service-ai',
    title: 'AI & Machine Learning',
    summary: 'Production-ready AI solutions to improve decision quality, speed, and operational efficiency.',
    route: '/services/ai',
    subServices: ['Predictive Analytics', 'AI Assistants', 'Computer Vision']
  },
  {
    id: 'service-sap',
    title: 'SAP Services',
    summary: 'Integrated SAP ERP programs for process design, implementation, and performance optimization.',
    route: '/services/sap',
    subServices: ['S/4HANA Migration', 'Finance & Logistics Integration', 'Application Management']
  },
  {
    id: 'service-cloud-devops',
    title: 'Cloud & DevOps',
    summary: 'Infrastructure automation and delivery engineering for faster, more resilient software delivery cycles.',
    route: '/services/cloud-devops',
    subServices: ['Cloud Architecture', 'CI/CD Automation', 'Infrastructure as Code']
  },
  {
    id: 'service-cybersecurity',
    title: 'Cyber Security',
    summary: 'Enterprise-grade security solutions to protect infrastructure, data, and workflows.',
    route: '/services/cybersecurity',
    subServices: ['Threat Intelligence', 'Network Security', 'Compliance controls']
  },
  {
    id: 'service-data-analytics',
    title: 'Data Analytics',
    summary: 'Transform enterprise data into actionable insights and strategic advantages.',
    route: '/services/data-analytics',
    subServices: ['Descriptive Analytics', 'Diagnostic Analytics', 'Predictive Modeling']
  },
  {
    id: 'service-product-development',
    title: 'Product Development',
    summary: 'Strategic product engineering from ideation to scale, combining superior UX with enterprise-grade reliability.',
    route: '/services/product-development',
    subServices: ['MVP Prototyping', 'UX/UI Design Systems', 'Agile Delivery']
  }
];

const ServicesOverview = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-slate-50 min-h-screen pt-4 pb-12">
      <Seo
        title="Services"
        description="Libsys IT services: AI & Machine Learning, SAP, Cloud & DevOps, Cybersecurity, Data Analytics, and Product Development — designed for enterprise scale and business outcomes."
        path="/services"
      />
      <section className="max-w-6xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mx-auto max-w-4xl mb-16"
        >
          <span className="text-red-tagline mb-1 inline-block">
            Strategic Capabilities
          </span>
          <h1 className="text-h2 text-primary mb-1">
            Enterprise IT Services
          </h1>
          <p className="text-slate-600 text-small max-w-2xl mx-auto leading-relaxed font-medium">
            Focused service lines designed to modernize operations, improve execution speed, and create measurable business value.
          </p>
        </motion.div>
 
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 items-stretch">
          {services.map((service, i) => (
            <motion.article
              key={service.id}
              id={service.id}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.45, delay: i * 0.06 }}
              className="relative overflow-hidden border border-[#236086]/20 bg-[#2e79a8] p-5 lg:p-6 hover:shadow-2xl hover:-translate-y-1 transition-all rounded-2xl flex flex-col group shadow-lg"
            >
              <div className="absolute inset-0 opacity-[0.15] bg-[radial-gradient(circle_at:18%_18%,white_0,transparent_44%)] pointer-events-none" />
 
              <h2 className="relative z-10 text-[20px] font-black text-white mb-2 leading-tight">
                {service.title}
              </h2>
              <p className="relative z-10 text-blue-50/90 text-subtext leading-relaxed mb-4 font-medium">
                {service.summary}
              </p>
 
              <ul className="relative z-10 mb-4 space-y-2">
                {service.subServices.map((sub) => (
                  <li key={sub} className="text-white text-tiny font-bold leading-relaxed flex items-start gap-2.5">
                    <span className="w-1.2 h-1.2 rounded-full bg-white/40 mt-1.5 flex-shrink-0" />
                    {sub}
                  </li>
                ))}
              </ul>
 
              <Link
                to={service.route}
                className="relative z-10 mt-auto inline-flex items-center gap-2 text-white font-black text-[10.5px] uppercase tracking-wider hover:gap-3 transition-all active:scale-95 bg-white/10 w-fit px-5 py-2.5 rounded-xl hover:bg-white/20"
              >
                Explore Service <ChevronRight className="w-4 h-4" />
              </Link>
            </motion.article>
          ))}
        </div>
      </section>
    </div>
  );
};

export default ServicesOverview;
